import React, { useRef, useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DependentSchema,
  DependentItemSchema,
  dependentSchema,
} from '@features/onboarding/schema/onboarding';
import {
  DEFAULT_DEPENDENTS_FORM,
  MAX_DEPENDENTS,
} from '@features/onboarding/constants';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { ApplicantFormItem } from '@features/onboarding/components/2-applicants-form/ApplicantFormItem';
import { ApplicantAddChildButton } from '@features/onboarding/components/2-applicants-form/ApplicantAddChildButton';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { ApplicantTagEnum } from '@features/onboarding/enums/applicant.enum';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { useUnderReviewStore } from 'src/store/under-review';
import { useAcceptanceChangeStore } from 'src/store/acceptance-change';
import { Form } from '@components/ui/form';
import { Separator } from '@components/ui/separator';
import { cn } from '@utils/index';

import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { FormMessageSpan } from '@components/ui/form';

export const ApplicantsForm = ({
  onSubmit,
  onInvalid,
  onChange,
}: FormStepProps) => {
  const { isUnderReview } = useUnderReviewStore();
  const { isApplicationAccepted } = useAcceptanceChangeStore();
  const { data, registerForm, unregisterForm } = useOnboardingStore();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const applicantRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lastAddedIndexRef = useRef<number | null>(null);

  const form = useForm<DependentSchema>({
    // @ts-ignore
    resolver: zodResolver(dependentSchema),
    defaultValues: {
      applicants: [DEFAULT_DEPENDENTS_FORM],
      ...data,
    },
  });

  const {
    watch,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    const subscription = watch((value) => {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        onChange(value as OnboardingSchema);
      }, 300); // Debounce with 300ms delay
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timerId); // Cleanup timer on unmount
    };
  }, [watch, onChange]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'applicants',
  });

  // Регистрируем форму для обновления через AI
  useEffect(() => {
    registerForm('applicantsForm', (data) => form.reset(data));

    return () => {
      unregisterForm('applicantsForm');
    };
  }, [form, registerForm, unregisterForm]);

  const handleAddDependent = (
    e: React.MouseEvent<HTMLButtonElement>,
    applicant: 'dependent' | 'spouse'
  ) => {
    e.preventDefault();
    if (
      fields.filter((e) => e.applicant === 'dependent').length >= MAX_DEPENDENTS
    ) {
      setErrorMessage(`Sorry, the maximum number of child dependents is ${MAX_DEPENDENTS}.`);
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }
    lastAddedIndexRef.current = fields.length;
    append({
      ...DEFAULT_DEPENDENTS_FORM,
      applicant,
    } as DependentItemSchema);
  };

  useEffect(() => {
    if (
      lastAddedIndexRef.current !== null &&
      applicantRefs.current[lastAddedIndexRef.current]
    ) {
      applicantRefs.current[lastAddedIndexRef.current]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      lastAddedIndexRef.current = null;
    }
  }, [fields.length]);

  const handleRemoveDependent = (index: number) => {
    remove(index);
    setErrorMessage(null);
  };

  return (
    <OnboardingFormStepLayout
      handleFormSubmit={form.handleSubmit(
        async (data: Partial<OnboardingSchema>) => {
          console.log('[DependentsForm] onSubmit called with:', data);
          return onSubmit(data);
        },
        (errors) => {
          console.log('[DependentsForm] onInvalid called with:', errors);
          return onInvalid(errors);
        }
      )}
      isFormValid={form.formState.isValid}
    >
      <div className="w-full lg:max-w-[95%]">
        <Form {...form}>
          <form
            // onSubmit={form.handleSubmit(onSubmit, onInvalid)}
            // onChange={form.handleSubmit(onChange)}
            className="flex flex-col gap-y-6"
          >
            {fields.map((field, index) => {
              const dependentNumber =
                field.applicant === ApplicantTagEnum.CHILD
                  ? fields.filter(
                      (f, idx) =>
                        f.applicant === ApplicantTagEnum.CHILD && idx <= index
                    ).length
                  : undefined;

              return (
                <ApplicantFormItem
                  key={field.id}
                  field={field}
                  index={index}
                  applicant={field.applicant}
                  totalDependents={fields.length}
                  dependentNumber={dependentNumber}
                  // @ts-ignore
                  form={form}
                  scrollRef={(el) => (applicantRefs.current[index] = el)}
                  remove={handleRemoveDependent}
                />
              );
            })}
            <div className={cn(
              "mt-6 bg-transparent",
              (isUnderReview || isApplicationAccepted) && "pointer-events-none"
            )}>
              {errorMessage && (
                <div className="-mt-3 mb-3">
                  <FormMessageSpan
                    text={errorMessage}
                  />
                </div>
              )}
              <p className="pb-4 text-lg xl:text-[22px] font-extrabold">
                Who else would you like to cover?
              </p>
              <Separator className="my-0" />
              {!fields.some(
                (field) => field.applicant === ApplicantTagEnum.SPOUSE
              ) && (
                <>
                  <ApplicantAddChildButton
                    title={'Add a Spouse'}
                    onClick={(e) =>
                      handleAddDependent(e, ApplicantTagEnum.SPOUSE)
                    }
                  />
                  <Separator className="my-0" />
                </>
              )}
              <ApplicantAddChildButton
                title="Add a Child or Dependent"
                onClick={(e) => handleAddDependent(e, ApplicantTagEnum.CHILD)}
              />
              <Separator className="my-0" />
            </div>
          </form>
        </Form>
      </div>
    </OnboardingFormStepLayout>
  );
};
